import {
  AlertTriangle,
  ArrowRight,
  Backpack,
  Binoculars,
  Clock3,
  Crosshair,
  Flame,
  HeartPulse,
  MapPin,
  Radio,
  Route,
  Scale,
  Send,
  ShieldCheck,
  Signal,
  SignalLow,
  Telescope,
  UserPlus,
  UsersRound,
} from "lucide-react";
import type { CSSProperties, SyntheticEvent } from "react";
import { useEffect, useMemo, useState } from "react";

import {
  formatDuration,
  formatDurationRange,
  formatQuantity,
} from "../lib/format";
import { coverageImpactWarning, isSurvivorAvailableForTemporaryWork } from "../lib/scheduling";
import {
  createSurfaceVisuals,
  expeditionMapPosition,
  type SurfaceArtKind,
  type SurfaceLocationVisual,
  type SurfacePoint,
} from "../lib/surfaceVisuals";
import type {
  Catalog,
  Expedition,
  GameCommand,
  GameState,
  ResourceKey,
  SkillKey,
  SurfaceRangeBand,
  Survivor,
  WorldLocation,
} from "../types";
import { SurvivorFigure } from "./SurvivorFigure";
import { SurvivorPortrait } from "./SurvivorPortrait";

interface SurfaceMapProps {
  state: GameState;
  catalog: Catalog;
  busy: boolean;
  onCommand: (command: GameCommand) => Promise<unknown>;
}

type WorldStyle = CSSProperties & Record<`--${string}`, string | number>;

const riverPaths = [
  "M 7 -8 C 17 17 38 26 42 47 C 46 68 65 76 73 108",
  "M 92 -8 C 78 17 62 25 65 45 C 68 64 47 78 39 108",
  "M 61 -8 C 49 18 55 30 46 45 C 34 64 43 82 29 108",
];

const contourPaths = [
  "M -8 25 C 12 13 28 16 43 27 S 76 37 108 20",
  "M -7 38 C 16 27 30 32 48 40 S 80 50 108 34",
  "M -8 62 C 17 49 31 55 51 63 S 82 72 108 55",
  "M -7 79 C 17 67 34 72 52 81 S 83 89 108 73",
];

const artKindLabels: Record<SurfaceArtKind, string> = {
  unknown: "Unverified site",
  pharmacy: "High-street pharmacy",
  petrol_station: "Service station",
  warehouse: "Distribution depot",
  farm: "Isolated farm",
  police_station: "Police station",
  housing_estate: "Housing estate",
  supermarket: "Regional supermarket",
  clinic: "Community clinic",
  waterworks: "Water treatment works",
  rail_yard: "Freight rail yard",
  broadcast_tower: "Broadcast tower",
  hospital: "Regional hospital",
  research_campus: "Research campus",
  military_depot: "Military depot",
  refuge_camp: "Refugee camp",
};

const rangeBandLabels: Record<SurfaceRangeBand, string> = {
  near: "Near",
  regional: "Regional",
  far: "Far",
};

const depletionLabels: Record<SurfaceLocationVisual["depletionBand"], string> = {
  intact: "Untouched",
  disturbed: "Disturbed",
  stripped: "Heavily picked",
  exhausted: "Exhausted",
};

const dangerLabels: Record<SurfaceLocationVisual["dangerBand"], string> = {
  unknown: "Danger unknown",
  low: "Low reported activity",
  elevated: "Elevated activity",
  severe: "Severe activity",
};

type ExpeditionPolicy = "cautious" | "balanced" | "bold";
type ExpeditionSkill = Extract<SkillKey, "scouting" | "combat" | "logistics">;
type ConditionBand = "ready" | "strained" | "high-risk";

const expeditionSkills = ["scouting", "combat", "logistics"] as const;

const expeditionPolicies = [
  {
    key: "cautious",
    name: "Cautious",
    summary: "Reduce danger and avoid commitments after contact.",
    tradeoff: "Favours rescue prospects; no extra focused salvage, and faction encounters remain guarded.",
    icon: ShieldCheck,
  },
  {
    key: "balanced",
    name: "Balanced",
    summary: "Use field judgement when contact changes the plan.",
    tradeoff: "Middle ground for salvage and rescue prospects; careful contact can improve faction standing.",
    icon: Scale,
  },
  {
    key: "bold",
    name: "Bold",
    summary: "Push deeper for the search priority.",
    tradeoff: "May secure extra priority salvage; rescue prospects fall and surface groups may resent it.",
    icon: Flame,
  },
] as const satisfies ReadonlyArray<{
  key: ExpeditionPolicy;
  name: string;
  summary: string;
  tradeoff: string;
  icon: typeof ShieldCheck;
}>;

const conditionRank: Record<ConditionBand, number> = {
  ready: 0,
  strained: 1,
  "high-risk": 2,
};

const conditionLabels: Record<ConditionBand, string> = {
  ready: "Ready",
  strained: "Strained",
  "high-risk": "High risk",
};

function titleFromKey(value: string): string {
  return value
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function effectiveExpeditionSkill(
  survivor: Survivor,
  skill: ExpeditionSkill,
  catalog: Pick<Catalog, "traits" | "equipment">,
): { base: number; bonus: number; total: number } {
  const base = survivor.skills[skill] ?? 0;
  const traitBonus = survivor.traits.reduce(
    (total, trait) => total + (catalog.traits?.[trait]?.skills[skill] ?? 0),
    0,
  );
  const equipmentBonus = survivor.equipment.reduce(
    (total, item) => {
      if (survivor.equipment_unavailable?.includes(item)) return total;
      const authored = catalog.equipment?.[item]?.skills[skill] ?? 0;
      const condition = survivor.equipment_condition?.[item] ?? 100;
      if (condition < 25) return total;
      return total + (condition >= 60 ? authored : Math.max(0, authored - 1));
    },
    0,
  );
  const bonus = traitBonus + equipmentBonus;
  return { base, bonus, total: Math.max(0, Math.min(8, base + bonus)) };
}

export function survivorConditionBand(survivor: Survivor): ConditionBand {
  const worstInjury = Math.max(0, ...survivor.injuries.map((injury) => injury.severity));
  if (
    survivor.health < 45
    || survivor.fatigue > 80
    || survivor.hunger > 82
    || survivor.thirst > 82
    || worstInjury >= 20
  ) {
    return "high-risk";
  }
  if (
    survivor.health < 72
    || survivor.morale < 45
    || survivor.fatigue > 58
    || survivor.hunger > 62
    || survivor.thirst > 62
    || survivor.injuries.length > 0
  ) {
    return "strained";
  }
  return "ready";
}

function survivorEffects(
  survivor: Survivor,
  catalog: Pick<Catalog, "traits" | "equipment">,
) {
  const traits = survivor.traits.map((trait) => ({
    kind: "Trait",
    name: trait,
    description: catalog.traits?.[trait]?.description ?? "No additional field effect recorded.",
  }));
  const equipment = survivor.equipment.map((item) => ({
    kind: "Gear",
    name: catalog.equipment?.[item]?.name ?? titleFromKey(item),
    description: catalog.equipment?.[item]?.description ?? "Carried personal equipment.",
  }));
  return [...traits, ...equipment];
}

export interface MissionReadiness {
  condition: ConditionBand;
  conditionLabel: string;
  conditionDetail: string;
  roundTripRange: [number, number];
  carryRange: [number, number];
  provisions: { food: number; water: number };
  strengths: Record<ExpeditionSkill, number>;
  sizeTradeoff: string;
}

export function missionReadinessFor(
  location: WorldLocation,
  team: Survivor[],
  catalog: Pick<Catalog, "traits" | "equipment">,
): MissionReadiness {
  const strengths = Object.fromEntries(
    expeditionSkills.map((skill) => [
      skill,
      team.reduce(
        (total, survivor) => total + effectiveExpeditionSkill(survivor, skill, catalog).total,
        0,
      ),
    ]),
  ) as Record<ExpeditionSkill, number>;
  const danger = location.observation.danger_range ?? [1, 6];
  const roundTripRange: [number, number] = [
    location.distance * 2 + 65 + danger[0] * 20,
    location.distance * 2 + 65 + danger[1] * 20,
  ];
  const carryRange: [number, number] = team.length
    ? [
        team.length * 300 + strengths.logistics * 40,
        team.length * 650 + strengths.logistics * 75,
      ]
    : [0, 0];
  const condition = team.reduce<ConditionBand>((worst, survivor) => {
    const next = survivorConditionBand(survivor);
    return conditionRank[next] > conditionRank[worst] ? next : worst;
  }, "ready");
  const lowestHealth = team.length ? Math.min(...team.map((survivor) => survivor.health)) : 0;
  const highestFatigue = team.length ? Math.max(...team.map((survivor) => survivor.fatigue)) : 0;
  const injuryCount = team.reduce((total, survivor) => total + survivor.injuries.length, 0);
  const conditionDetail = team.length
    ? `Lowest health ${lowestHealth}%; highest fatigue ${highestFatigue}%${injuryCount ? `; ${injuryCount} active ${injuryCount === 1 ? "injury" : "injuries"}` : ""}.`
    : "Select at least one survivor to assess the team.";
  const sizeTradeoff = team.length === 0
    ? "No team selected."
    : team.length === 1
      ? "Lean team: lowest provision cost, but no backup if the survivor is hurt."
      : team.length === 2
        ? "Covered team: broader skills and one backup at a moderate provision cost."
        : "Full team: widest skill coverage and resilience, but three fewer hands remain in the bunker.";
  const provisionMultiplier = location.range_band === "far"
    ? 2.25
    : location.range_band === "regional"
      ? 1.5
      : 1;
  return {
    condition,
    conditionLabel: team.length ? conditionLabels[condition] : "Awaiting team",
    conditionDetail,
    roundTripRange,
    carryRange,
    provisions: {
      food: Math.ceil(team.length * 220 * provisionMultiplier),
      water: Math.ceil(team.length * 300 * provisionMultiplier),
    },
    strengths,
    sizeTradeoff,
  };
}

function routePath(
  start: SurfacePoint,
  destination: SurfacePoint,
  bend: number,
): string {
  const deltaX = destination.x - start.x;
  const deltaY = destination.y - start.y;
  const length = Math.max(1, Math.hypot(deltaX, deltaY));
  const controlX = (start.x + destination.x) / 2 + (-deltaY / length) * bend;
  const controlY = (start.y + destination.y) / 2 + (deltaX / length) * bend;
  return `M ${start.x} ${start.y} Q ${controlX.toFixed(2)} ${controlY.toFixed(2)} ${destination.x} ${destination.y}`;
}

function artLoadFailed(event: SyntheticEvent<HTMLImageElement>) {
  event.currentTarget.hidden = true;
  event.currentTarget.parentElement?.classList.add("location-art-failed");
}

function LocationArtwork({
  visual,
  variant,
  eager = false,
  loadImage = true,
}: {
  visual: SurfaceLocationVisual;
  variant: "marker" | "plate";
  eager?: boolean;
  loadImage?: boolean;
}) {
  return (
    <span
      className={`location-art location-art-${variant} landmark-kind-${visual.artKind} art-variant-${visual.artVariant}`}
      aria-hidden="true"
      data-art-variant={visual.artVariant}
      style={{
        "--location-focus-x": `${visual.artFocusX}%`,
        "--location-focus-y": `${visual.artFocusY}%`,
      } as CSSProperties}
    >
      <span className="location-art-fallback"><MapPin /></span>
      {loadImage && (
        <img
          key={`${visual.artUrl}:${visual.artVariant}:${variant}`}
          src={visual.artUrl}
          alt=""
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          draggable={false}
          onError={artLoadFailed}
        />
      )}
      <span className="location-art-wear" />
    </span>
  );
}

function reportAgeLabel(ageSeconds: number): string {
  return ageSeconds < 10 ? "Observed just now" : `${formatDuration(ageSeconds)} old`;
}

function expeditionScene(
  expedition: Expedition,
  state: GameState,
  location: WorldLocation,
  visual: SurfaceLocationVisual,
  bunker: SurfacePoint,
) {
  // The map is an operations board, not live tracking. Public away teams sit
  // at a fixed route estimate so canonical phase timing cannot be inferred
  // from a moving marker.
  const point = !["complete", "lost"].includes(expedition.phase)
    ? {
        x: Math.round((bunker.x + visual.x) * 50) / 100,
        y: Math.round((bunker.y + visual.y) * 50) / 100,
      }
    : expeditionMapPosition(expedition, location, bunker, visual);
  const people = expedition.survivor_ids
    .map((id) => state.survivors.find((survivor) => survivor.id === id))
    .filter((survivor) => survivor != null);
  return { expedition, location, visual, point, people };
}

function expeditionContactLabel(expedition: Expedition): string {
  if (expedition.contact?.status === "homeward_signal") return "Homeward signal";
  if (expedition.contact?.status === "overdue") return "Overdue · no contact";
  return "Beyond radio contact";
}

function expeditionReturnWindow(expedition: Expedition): string {
  const range = expedition.contact?.return_eta_range_seconds;
  return range ? `${formatDurationRange(range)} return window` : "Return time unknown";
}

function SurvivorRosterOption({
  survivor,
  catalog,
  checked,
  disabled,
  coverageWarning,
  onChange,
}: {
  survivor: Survivor;
  catalog: Catalog;
  checked: boolean;
  disabled: boolean;
  coverageWarning?: string | null;
  onChange: () => void;
}) {
  const condition = survivorConditionBand(survivor);
  const coverageWarningId = `expedition-coverage-${survivor.id}`;
  const skills = Object.fromEntries(
    expeditionSkills.map((skill) => [skill, effectiveExpeditionSkill(survivor, skill, catalog)]),
  ) as Record<ExpeditionSkill, ReturnType<typeof effectiveExpeditionSkill>>;
  const effects = survivorEffects(survivor, catalog);
  const injuries = survivor.injuries.map((injury) => injury.name).join(", ");

  return (
    <label
      className={`surface-roster-option condition-${condition} ${checked ? "checked" : ""} ${coverageWarning ? "has-coverage-impact" : ""}`}
      title={coverageWarning ?? undefined}
    >
      <input
        type="checkbox"
        aria-label={`${survivor.name}, expedition team`}
        aria-describedby={coverageWarning ? coverageWarningId : undefined}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <SurvivorPortrait
        survivor={survivor}
        className="surface-roster-portrait"
        detail="thumb"
      />
      <span className="surface-roster-identity">
        <strong>{survivor.name}</strong>
        <small>{survivor.role}</small>
      </span>
      <span className={`surface-roster-condition condition-${condition}`}>
        {conditionLabels[condition]}
      </span>
      <span className="surface-roster-skills" aria-label={`${survivor.name} effective expedition strengths`}>
        <span>
          <Binoculars aria-hidden="true" />
          <small>Scouting</small>
          <strong>{skills.scouting.total}</strong>
          {skills.scouting.bonus !== 0 && <em>{skills.scouting.bonus > 0 ? "+" : ""}{skills.scouting.bonus}</em>}
        </span>
        <span>
          <Crosshair aria-hidden="true" />
          <small>Combat</small>
          <strong>{skills.combat.total}</strong>
          {skills.combat.bonus !== 0 && <em>{skills.combat.bonus > 0 ? "+" : ""}{skills.combat.bonus}</em>}
        </span>
        <span>
          <Backpack aria-hidden="true" />
          <small>Logistics</small>
          <strong>{skills.logistics.total}</strong>
          {skills.logistics.bonus !== 0 && <em>{skills.logistics.bonus > 0 ? "+" : ""}{skills.logistics.bonus}</em>}
        </span>
      </span>
      <span className="surface-roster-vitals">
        <span><HeartPulse aria-hidden="true" />Health {survivor.health}%</span>
        <span>Morale {survivor.morale}%</span>
        <span>Fatigue {survivor.fatigue}%</span>
        <span>Hunger {survivor.hunger}%</span>
        <span>Thirst {survivor.thirst}%</span>
      </span>
      {injuries && <span className="surface-roster-injuries"><AlertTriangle aria-hidden="true" />{injuries}</span>}
      {coverageWarning && <span className="surface-roster-coverage-warning" id={coverageWarningId}><AlertTriangle aria-hidden="true" />Leaving now may reduce protected room coverage until relief is found.</span>}
      <span className="surface-roster-effects">
        {effects.map((effect, index) => (
          <span className="surface-roster-effect" key={`${effect.kind}-${effect.name}-${index}`}>
            <em>{effect.kind}</em>
            <span>
              <strong>{effect.name}</strong>
              <small>{effect.description}</small>
            </span>
          </span>
        ))}
      </span>
    </label>
  );
}

function MissionReadinessPreview({
  readiness,
  catalog,
  focus,
  policy,
  teamSize,
}: {
  readiness: MissionReadiness;
  catalog: Catalog;
  focus: ResourceKey;
  policy: ExpeditionPolicy;
  teamSize: number;
}) {
  const policyDefinition = expeditionPolicies.find((option) => option.key === policy)!;
  const PolicyIcon = policyDefinition.icon;
  return (
    <section
      className={`mission-readiness condition-${readiness.condition}`}
      aria-live="polite"
      aria-label="Mission readiness estimate"
    >
      <header>
        <span>
          <small>Mission readiness · planning estimate</small>
          <strong>{teamSize ? `${teamSize}-person field team` : "Select a field team"}</strong>
        </span>
        <em>{readiness.conditionLabel}</em>
      </header>
      <p className="mission-team-tradeoff">{readiness.sizeTradeoff}</p>
      <div className="mission-preview-grid">
        <article>
          <Clock3 aria-hidden="true" />
          <span>Estimated round trip</span>
          <strong>{formatDurationRange(readiness.roundTripRange)}</strong>
          <small>Travel plus the reported danger band.</small>
        </article>
        <article>
          <Backpack aria-hidden="true" />
          <span>Likely carrying range</span>
          <strong>
            {teamSize
              ? `≈ ${formatQuantity(readiness.carryRange[0], catalog.quantity_scale)}–${formatQuantity(readiness.carryRange[1], catalog.quantity_scale)} units`
              : "Awaiting team"}
          </strong>
          <small>Pack-and-logistics estimate, not guaranteed salvage.</small>
        </article>
        <article>
          <HeartPulse aria-hidden="true" />
          <span>Team condition</span>
          <strong>{readiness.conditionLabel}</strong>
          <small>{readiness.conditionDetail}</small>
        </article>
        <article>
          <PolicyIcon aria-hidden="true" />
          <span>Risk posture</span>
          <strong>{policyDefinition.name}</strong>
          <small>{policyDefinition.tradeoff}</small>
        </article>
      </div>
      <div className="mission-strengths" aria-label="Combined effective expedition strengths">
        <span><Binoculars aria-hidden="true" />Scouting <strong>{readiness.strengths.scouting}</strong></span>
        <span><Crosshair aria-hidden="true" />Combat <strong>{readiness.strengths.combat}</strong></span>
        <span><Backpack aria-hidden="true" />Logistics <strong>{readiness.strengths.logistics}</strong></span>
      </div>
      <div className="mission-costs">
        <span>Search priority <strong>{catalog.resources[focus].name}</strong></span>
        <span>
          Provisions <strong>{formatQuantity(readiness.provisions.food, catalog.quantity_scale)} food · {formatQuantity(readiness.provisions.water, catalog.quantity_scale)} water</strong>
        </span>
      </div>
      <p className="mission-estimate-note">
        <AlertTriangle aria-hidden="true" />These are planning estimates from current reports and visible team condition. Encounters and salvage remain uncertain.
      </p>
    </section>
  );
}

export function SurfaceMap({ state, catalog, busy, onCommand }: SurfaceMapProps) {
  const [selectedId, setSelectedId] = useState(state.locations[0]?.id ?? "");
  const [roster, setRoster] = useState<string[]>([]);
  const [focus, setFocus] = useState<ResourceKey>("food");
  const [policy, setPolicy] = useState<ExpeditionPolicy>("balanced");
  const [rosterSearch, setRosterSearch] = useState("");
  const [rosterPage, setRosterPage] = useState(1);
  const [rescuePosture, setRescuePosture] = useState<"welcome" | "aid_only">("welcome");
  const [rangeFilter, setRangeFilter] = useState<"all" | SurfaceRangeBand>("all");
  const [scanBand, setScanBand] = useState<SurfaceRangeBand>("near");
  const [scanWorkers, setScanWorkers] = useState<string[]>([]);
  const [scanRadioId, setScanRadioId] = useState("");
  const [replacementId, setReplacementId] = useState("");
  const selected =
    state.locations.find((item) => item.id === selectedId) ?? state.locations[0];
  const available = useMemo(
    () =>
      state.survivors.filter(isSurvivorAvailableForTemporaryWork),
    [state.survivors],
  );
  const availableIds = useMemo(
    () => new Set(available.map((survivor) => survivor.id)),
    [available],
  );
  const validRoster = useMemo(
    () => roster.filter((survivorId) => availableIds.has(survivorId)),
    [availableIds, roster],
  );
  useEffect(() => {
    setRoster((current) => {
      const next = current.filter((survivorId) => availableIds.has(survivorId));
      return next.length === current.length ? current : next;
    });
  }, [availableIds]);
  const rosterOptions = useMemo(() => [...available]
    .filter((person) => `${person.name} ${person.role}`.toLowerCase().includes(rosterSearch.trim().toLowerCase()))
    .sort((left, right) => (
      effectiveExpeditionSkill(right, "scouting", catalog).total - effectiveExpeditionSkill(left, "scouting", catalog).total
      || effectiveExpeditionSkill(right, "logistics", catalog).total - effectiveExpeditionSkill(left, "logistics", catalog).total
      || left.name.localeCompare(right.name)
    )), [available, catalog, rosterSearch]);
  const rosterPageSize = 20;
  const rosterTotalPages = Math.max(1, Math.ceil(rosterOptions.length / rosterPageSize));
  const visibleRosterOptions = rosterOptions.slice((rosterPage - 1) * rosterPageSize, rosterPage * rosterPageSize);
  const radioSpaces = useMemo(
    () => state.site.spaces
      .filter((space) => (
        space.room?.type === "radio"
        && space.room.status === "operational"
        && space.room.enabled !== false
        && (state.surface_intel?.active_radio_space_ids
          ? state.surface_intel.active_radio_space_ids.includes(space.id)
          : space.room.operation?.powered !== false)
      ))
      .sort((left, right) => (
        (right.room?.installation_level ?? 1) - (left.room?.installation_level ?? 1)
        || left.id.localeCompare(right.id)
      )),
    [state.site.spaces, state.surface_intel?.active_radio_space_ids],
  );
  const availableScanBands = state.surface_intel?.available_scan_bands ?? [];
  const activeScanTask = state.tasks.find((task) => (
    task.id === state.surface_intel?.active_scan_task_id || task.kind === "surface_scan"
  ));
  const selectedRadio = radioSpaces.find((space) => space.id === scanRadioId) ?? radioSpaces[0];
  const selectedRadioTier = selectedRadio?.room?.tier
    ?? ((selectedRadio?.room?.units ?? 1) >= 4 ? "expanded" : (selectedRadio?.room?.units ?? 1) >= 2 ? "standard" : "compact");
  const selectedRadioWorkerLimit = selectedRadio
    ? catalog.rooms.radio?.workers_by_tier?.[selectedRadioTier] ?? catalog.rooms.radio?.workers ?? 2
    : 0;
  const supportedReportCapacity = state.surface_intel?.supported_report_capacity ?? state.locations.length;
  const reportsKnown = state.surface_intel?.reports_known ?? state.locations.length;
  const needsReplacement = supportedReportCapacity > 0 && reportsKnown >= supportedReportCapacity;
  const activeLocationIds = new Set(state.expeditions
    .filter((expedition) => !["complete", "lost"].includes(expedition.phase))
    .map((expedition) => expedition.location_id));
  const replacementCandidates = state.locations.filter((location) => !activeLocationIds.has(location.id));
  const visibleLocations = state.locations.filter((location) => (
    rangeFilter === "all" || (location.range_band ?? "near") === rangeFilter
  ));
  const mapScale = state.locations.some((location) => location.range_band === "far")
    ? "12 km"
    : state.locations.some((location) => location.range_band === "regional")
      ? "6 km"
      : "2 km";
  useEffect(() => {
    if (!scanRadioId && radioSpaces[0]) setScanRadioId(radioSpaces[0].id);
  }, [radioSpaces, scanRadioId]);
  useEffect(() => {
    if (availableScanBands.length && !availableScanBands.includes(scanBand)) {
      setScanBand(availableScanBands[0]!);
    }
  }, [availableScanBands, scanBand]);
  useEffect(() => {
    setScanWorkers((current) => current.filter((id) => available.some((person) => person.id === id)).slice(0, selectedRadioWorkerLimit));
  }, [available, selectedRadioWorkerLimit]);
  useEffect(() => setRosterPage(1), [rosterSearch]);
  const visuals = useMemo(
    () =>
      createSurfaceVisuals({
        visualSeed: state.site.visual_seed,
        gameTime: state.game_time,
        locations: state.locations,
        threat: state.threat,
        activeCrisis: Boolean(state.active_crisis),
      }),
    [
      state.active_crisis,
      state.game_time,
      state.locations,
      state.site.visual_seed,
      state.threat,
    ],
  );

  if (!selected) return null;
  const selectedLocation = selected;
  const selectedVisual = visuals.locations[selected.id];
  if (!selectedVisual) return null;
  const selectedReportLocked = activeScanTask?.replace_location_id === selected.id;
  const rosterSurvivors = validRoster
    .map((id) => state.survivors.find((survivor) => survivor.id === id))
    .filter((survivor): survivor is Survivor => survivor != null);
  const readiness = missionReadinessFor(selectedLocation, rosterSurvivors, catalog);

  const activeExpeditionScenes = state.expeditions
    .filter((item) => !["complete", "lost"].includes(item.phase))
    .map((expedition) => {
      const location = state.locations.find(
        (item) => item.id === expedition.location_id,
      );
      const visual = location ? visuals.locations[location.id] : undefined;
      return location && visual
        ? expeditionScene(expedition, state, location, visual, visuals.bunker)
        : null;
    })
    .filter((scene) => scene != null);
  const recentFieldRecords = state.expeditions
    .filter((item) => item.phase === "complete" && (item.field_decision || item.equipment_found))
    .slice(-3)
    .reverse();
  const legacyFiniteRecruitment = state.recruitment?.limit != null;
  const recruitmentAvailable = state.recruitment?.available
    ?? ((state.recruitment?.remaining ?? 1) > 0);
  const recruitmentCooldown = state.recruitment?.cooldown_seconds_remaining ?? 0;

  const palette = state.site.archetype.palette;
  const worldStyle = {
    "--world-dark": palette[0] ?? "#111714",
    "--world-ground": palette[1] ?? "#2d352f",
    "--world-mid": palette[2] ?? "#697366",
    "--world-signal": palette[3] ?? "#d29b56",
    "--world-danger": palette[4] ?? "#ad4f3d",
  } as WorldStyle;

  async function dispatch() {
    await onCommand({
      type: "start_expedition",
      location_id: selectedLocation.id,
      survivor_ids: validRoster,
      focus,
      policy,
      rescue_posture: rescuePosture,
    });
    setRoster([]);
  }

  async function dispatchScan() {
    if (!selectedRadio || scanWorkers.length === 0) return;
    await onCommand({
      type: "start_surface_scan",
      radio_space_id: selectedRadio.id,
      range_band: scanBand,
      worker_ids: scanWorkers,
      ...(needsReplacement && replacementId ? { replace_location_id: replacementId } : {}),
    });
    setScanWorkers([]);
    setReplacementId("");
  }

  return (
    <section className="surface-layout surface-world-layout">
      <div className="surface-mobile-overview">
        <header>
          <span className="eyebrow">Operations map · sector {visuals.sector}</span>
          <h2>Nothing stays safe for long.</h2>
          <p>Reports are estimates. Their age and source matter.</p>
        </header>
        <nav className="surface-mobile-range-filters" aria-label="Filter mobile surface reports by range">
          {(["all", "near", "regional", "far"] as const).map((band) => (
            <button
              type="button"
              aria-pressed={rangeFilter === band}
              className={rangeFilter === band ? "active" : ""}
              key={band}
              onClick={() => {
                setRangeFilter(band);
                if (band !== "all") {
                  const first = state.locations.find((location) => (location.range_band ?? "near") === band);
                  if (first) setSelectedId(first.id);
                }
              }}
            >
              {band === "all" ? "All" : rangeBandLabels[band]}
              <small>{band === "all" ? state.locations.length : state.locations.filter((location) => (location.range_band ?? "near") === band).length}</small>
            </button>
          ))}
        </nav>
        <div className="surface-mobile-report-list" aria-label="Surface reports">
          {visibleLocations.map((location) => (
            <button
              aria-pressed={location.id === selected.id}
              className={location.id === selected.id ? "selected" : ""}
              key={location.id}
              onClick={() => setSelectedId(location.id)}
              type="button"
            >
              <strong>{location.name}</strong>
              <small>{formatDuration(location.distance)} · {location.observation.confidence}% confidence</small>
            </button>
          ))}
        </div>
      </div>
      <div
        className={`world-map surface-world-map ${visibleLocations.length > 6 ? "reports-crowded" : ""} terrain-${visuals.terrainVariant} relief-${visuals.reliefVariant} phase-${visuals.dayPhase} weather-${visuals.weather}`}
        style={worldStyle}
        data-weather={visuals.weather}
        data-day-phase={visuals.dayPhase}
      >
        <div className="surface-landscape" aria-hidden="true">
          <div className="surface-horizon" />
          <div className="surface-relief relief-back" />
          <div className="surface-relief relief-front" />
          <div className="surface-fields" />
        </div>
        <div className="surface-range-rings" aria-hidden="true">
          <i className="range-ring-near"><span>Near</span></i>
          <i className="range-ring-regional"><span>Regional</span></i>
          <i className="range-ring-far"><span>Far</span></i>
        </div>

        <svg
          className="surface-cartography"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {contourPaths.map((path, index) => (
            <path className="surface-contour" d={path} key={path} data-index={index} />
          ))}
          <path
            className="surface-river river-shadow"
            d={riverPaths[visuals.riverVariant - 1] ?? riverPaths[0]}
          />
          <path
            className="surface-river river-water"
            d={riverPaths[visuals.riverVariant - 1] ?? riverPaths[0]}
          />
          {visibleLocations.map((location) => {
            const visual = visuals.locations[location.id];
            if (!visual) return null;
            return (
              <g key={location.id}>
                <path
                  className="surface-road road-shadow"
                  d={routePath(visuals.bunker, visual, visual.routeBend)}
                />
                <path
                  className="surface-road road-track"
                  d={routePath(visuals.bunker, visual, visual.routeBend)}
                />
              </g>
            );
          })}
        </svg>

        <div className="surface-forest-layer" aria-hidden="true">
          {visuals.forests.map((forest, index) => (
            <span
              className={`forest-cluster forest-variant-${forest.variant}`}
              style={{
                left: `${forest.x}%`,
                top: `${forest.y}%`,
                "--cluster-scale": forest.scale,
                "--cluster-rotation": `${forest.rotation}deg`,
              } as WorldStyle}
              key={`forest-${index}`}
            >
              <i /><i /><i /><i /><i />
            </span>
          ))}
        </div>

        <div className="surface-settlement-layer" aria-hidden="true">
          {visuals.settlements.map((settlement, index) => (
            <span
              className={`settlement-cluster settlement-variant-${settlement.variant}`}
              style={{
                left: `${settlement.x}%`,
                top: `${settlement.y}%`,
                "--cluster-scale": settlement.scale,
                "--cluster-rotation": `${settlement.rotation}deg`,
              } as WorldStyle}
              key={`settlement-${index}`}
            >
              <i /><i /><i /><b />
            </span>
          ))}
        </div>

        {visuals.threat && state.threat && (
          <>
            <div
              className={`surface-horde horde-edge-${visuals.threat.edge} horde-${visuals.threat.kind} proximity-${visuals.threat.proximity} density-${visuals.threat.density} intel-${visuals.threat.confidenceBand}`}
              aria-hidden="true"
            >
              <span className="horde-haze" />
              <span className="horde-line">
                {Array.from({ length: 14 }, (_, index) => <i key={index} />)}
              </span>
            </div>
            <div className={`surface-threat-readout threat-${visuals.threat.proximity}`}>
              <AlertTriangle size={15} aria-hidden="true" />
              <span>
                <small>{visuals.threat.kind} movement · {visuals.threat.edge} boundary</small>
                <strong>{formatDurationRange(state.threat.eta_range_seconds)} estimated contact</strong>
              </span>
            </div>
          </>
        )}

        <div className="surface-weather" aria-hidden="true">
          <span className="weather-mist mist-one" />
          <span className="weather-mist mist-two" />
          <span className="weather-rainfall">
            {Array.from({ length: 16 }, (_, index) => <i key={index} />)}
          </span>
          <span className="weather-grain" />
          <span className="weather-vignette" />
        </div>

        <div className="map-title">
          <span className="eyebrow">Operations map · sector {visuals.sector}</span>
          <h2>Nothing stays safe for long.</h2>
          <p>Reports are estimates. Their age and source matter.</p>
          <small>{visuals.weather} · {visuals.dayPhase} · {state.site.geology.name}</small>
        </div>
        <nav className="surface-range-filters" aria-label="Filter surface reports by range">
          {(["all", "near", "regional", "far"] as const).map((band) => (
            <button
              type="button"
              aria-pressed={rangeFilter === band}
              className={rangeFilter === band ? "active" : ""}
              key={band}
              onClick={() => {
                setRangeFilter(band);
                if (band !== "all") {
                  const first = state.locations.find((location) => (location.range_band ?? "near") === band);
                  if (first) setSelectedId(first.id);
                }
              }}
            >
              {band === "all" ? "All" : rangeBandLabels[band]}
              <small>{band === "all" ? state.locations.length : state.locations.filter((location) => (location.range_band ?? "near") === band).length}</small>
            </button>
          ))}
        </nav>

        <svg
          className="expedition-route-layer"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {activeExpeditionScenes.map(({ expedition, visual }) => (
            <path
              className={`expedition-map-route phase-away policy-${expedition.policy}`}
              d={routePath(visuals.bunker, visual, visual.routeBend)}
              key={expedition.id}
            />
          ))}
        </svg>

        {activeExpeditionScenes.map(({ expedition, point, people }) => (
          <div
            className="surface-team phase-away"
            style={{ left: `${point.x}%`, top: `${point.y}%` }}
            aria-hidden="true"
            key={expedition.id}
          >
            <span className="team-signal" />
            {people.map((person, index) => (
              <SurvivorFigure
                survivor={person}
                activity="walk"
                className={`surface-expedition-figure expedition-figure-${index}`}
                key={person.id}
              />
            ))}
          </div>
        ))}

        <div
          className={`bunker-marker bunker-power-${state.site.power}`}
          style={{ left: `${visuals.bunker.x}%`, top: `${visuals.bunker.y}%` }}
        >
          <span><Radio size={16} /></span>
          <strong>{state.site.name}</strong>
          <small>Community signal</small>
          <i />
        </div>

        {visibleLocations.map((location) => {
          const visual = visuals.locations[location.id];
          if (!visual) return null;
          const expedition = activeExpeditionScenes.find(
            (scene) => scene.location.id === location.id,
          )?.expedition;
          return (
            <button
              className={`location-marker band-${location.range_band ?? "near"} ${activeScanTask?.replace_location_id === location.id ? "scan-locked" : ""} ${location.id === selected.id ? "selected" : ""} confidence-${location.observation.confidence < 40 ? "low" : "known"} intel-${visual.confidenceBand} age-${visual.ageBand} danger-${visual.dangerBand} depletion-${visual.depletionBand}`}
              style={{ left: `${visual.x}%`, top: `${visual.y}%` }}
              key={location.id}
              aria-label={`${location.name}. ${location.observation.confidence}% confidence. ${formatDuration(location.distance)} travel.`}
              aria-pressed={location.id === selected.id}
              data-art-kind={visual.artKind}
              data-intel-band={visual.confidenceBand}
              data-age-band={visual.ageBand}
              data-danger-band={visual.dangerBand}
              data-depletion-band={visual.depletionBand}
              data-range-band={location.range_band ?? "near"}
              onClick={() => setSelectedId(location.id)}
            >
              <LocationArtwork
                visual={visual}
                variant="marker"
                loadImage={location.id === selected.id}
              />
              <strong>{location.name}</strong>
              <small>{formatDuration(location.distance)} out · {location.observation.confidence}% confidence</small>
              {expedition && <em className="marker-expedition phase-away">{expeditionContactLabel(expedition)}</em>}
            </button>
          );
        })}

        <div className="map-scale"><span>0</span><i /><span>{mapScale}</span></div>
        <div className="map-legend">
          <span><Signal size={13} />recent observation</span>
          <span><SignalLow size={13} />unconfirmed report</span>
        </div>
      </div>

      <aside className="expedition-planner panel-surface">
        <div className="expedition-planner-scroll">
        {state.surface_intel && (
          <section className="surface-scan-console" aria-labelledby="surface-scan-title">
            <header>
              <Telescope aria-hidden="true" />
              <span><small>Radio survey</small><strong id="surface-scan-title">Find another report</strong></span>
              <em>{reportsKnown}/{supportedReportCapacity} supported</em>
            </header>
            <dl>
              <div><dt>Best installed radio</dt><dd>{state.surface_intel.highest_installed_radio_level || "None"}</dd></div>
              <div><dt>Active radio</dt><dd>{state.surface_intel.active_radio_level || "Offline"}</dd></div>
              <div><dt>Scan reach</dt><dd>{availableScanBands.length ? availableScanBands.map((band) => rangeBandLabels[band]).join(" · ") : "Unavailable"}</dd></div>
            </dl>
            {activeScanTask ? (
              <div className="active-surface-scan" role="status">
                <Clock3 aria-hidden="true" />
                <span>
                  <small>{rangeBandLabels[activeScanTask.range_band ?? "near"]} survey underway</small>
                  <strong>{formatDuration(activeScanTask.seconds_left)} remaining</strong>
                </span>
                <button type="button" disabled={busy} onClick={() => onCommand({ type: "cancel_task", task_id: activeScanTask.id })}>Cancel</button>
              </div>
            ) : (
              <div className="surface-scan-form">
                <label>
                  <span>Radio room</span>
                  <select value={selectedRadio?.id ?? ""} disabled={busy || radioSpaces.length === 0} onChange={(event) => setScanRadioId(event.currentTarget.value)}>
                    {radioSpaces.map((space) => <option value={space.id} key={space.id}>{space.id} · Level {space.room?.installation_level ?? 1}</option>)}
                  </select>
                </label>
                <label>
                  <span>Range band</span>
                  <select value={scanBand} disabled={busy || availableScanBands.length === 0} onChange={(event) => setScanBand(event.currentTarget.value as SurfaceRangeBand)}>
                    {availableScanBands.map((band) => <option value={band} key={band}>{rangeBandLabels[band]}</option>)}
                  </select>
                </label>
                <label className="scan-worker-select">
                  <span>Operators · choose up to {selectedRadioWorkerLimit}</span>
                  <select
                    multiple
                    size={Math.max(2, Math.min(5, available.length))}
                    value={scanWorkers}
                    disabled={busy || available.length === 0}
                    onChange={(event) => {
                      const ids = Array.from(event.currentTarget.selectedOptions, (option) => option.value).slice(0, selectedRadioWorkerLimit);
                      setScanWorkers(ids);
                    }}
                  >
                    {[...available].sort((left, right) => right.skills.scouting - left.skills.scouting || left.name.localeCompare(right.name)).map((person) => (
                      <option value={person.id} key={person.id}>{person.name} · {person.skills.scouting}/5 scouting</option>
                    ))}
                  </select>
                </label>
                {needsReplacement && (
                  <label>
                    <span>Replace an old report</span>
                    <select value={replacementId} disabled={busy || replacementCandidates.length === 0} onChange={(event) => setReplacementId(event.currentTarget.value)}>
                      <option value="">Choose a report…</option>
                      {replacementCandidates.map((location) => <option value={location.id} key={location.id}>{location.name} · {rangeBandLabels[location.range_band ?? "near"]}</option>)}
                    </select>
                    <small>Reports being visited by an away team stay locked.</small>
                  </label>
                )}
                <button
                  type="button"
                  className="secondary-button"
                  disabled={busy || !selectedRadio || availableScanBands.length === 0 || scanWorkers.length === 0 || scanWorkers.length > selectedRadioWorkerLimit || (needsReplacement && !replacementId)}
                  onClick={() => void dispatchScan()}
                >
                  <Telescope aria-hidden="true" />Start {rangeBandLabels[scanBand]} scan
                </button>
              </div>
            )}
          </section>
        )}
        <div className="inspector-kicker"><Binoculars size={14} />Surface report</div>
        <h2>{selected.name}<small className={`range-band-chip band-${selected.range_band ?? "near"}`}>{rangeBandLabels[selected.range_band ?? "near"]}</small></h2>
        <figure
          className={`location-inspection-plate intel-${selectedVisual.confidenceBand} age-${selectedVisual.ageBand} danger-${selectedVisual.dangerBand} depletion-${selectedVisual.depletionBand}`}
          data-art-kind={selectedVisual.artKind}
        >
          <LocationArtwork visual={selectedVisual} variant="plate" eager />
          <figcaption>
            <span>{artKindLabels[selectedVisual.artKind]}</span>
            <strong>{reportAgeLabel(selectedVisual.reportAgeSeconds)}</strong>
          </figcaption>
          <div className="location-condition-strip">
            <span>{dangerLabels[selectedVisual.dangerBand]}</span>
            <span>Salvage: {depletionLabels[selectedVisual.depletionBand]}</span>
          </div>
        </figure>
        <p>{selected.description}</p>
        {selectedReportLocked && <p className="warning-copy"><Telescope size={14} />This report is reserved for replacement when the active scan completes. Expeditions cannot depart for it.</p>}
        {(selected.observation.approach || selected.observation.warning) && (
          <div className="surface-route-intel" data-variant-id={selected.variant_id ?? "legacy"}>
            {selected.observation.approach && <article><Route size={14} /><span><small>Approach</small><strong>{selected.observation.approach}</strong></span></article>}
            {selected.observation.warning && <article className="route-warning"><AlertTriangle size={14} /><span><small>Field warning</small><strong>{selected.observation.warning}</strong></span></article>}
          </div>
        )}
        <div className="intel-card">
          <div>
            <span>Source</span>
            <strong>{selected.observation.source}</strong>
            <small>{reportAgeLabel(selectedVisual.reportAgeSeconds)}</small>
          </div>
          <div><span>Confidence</span><strong>{selected.observation.confidence}%</strong></div>
          <div><span>Likely danger</span><strong>{selected.observation.danger_range?.join("–") ?? "Unknown"} / 6</strong></div>
          <div><span>Round trip</span><strong>≈ {formatDuration(selected.distance * 2 + 120)}</strong></div>
        </div>
        <div className="likely-resources">
          <span>Likely salvage</span>
          {selected.observation.likely_resources?.length ? (
            selected.observation.likely_resources.map((resource) => (
              <b key={resource}>{catalog.resources[resource].name}</b>
            ))
          ) : (
            <em>Nothing reliable</em>
          )}
        </div>
        {state.factions.length > 0 && (
          <section className="faction-intel-list" aria-label="Surface factions">
            <header><Radio size={13} /><span>Surface voices · {state.factions.filter((faction) => faction.known).length}/{state.factions.length} identified</span></header>
            {state.factions.map((faction) => (
              <article className={`faction-intel ${faction.known ? "known" : "unknown"}`} key={faction.id}>
                <SignalLow size={14} />
                <div>
                  <span>{faction.known ? "Known surface group" : "Unidentified radio traffic"}</span>
                  <strong>{faction.name}</strong>
                  <small>{faction.summary}</small>
                </div>
                <em>
                  {faction.known
                    ? `${faction.standing >= 0 ? "+" : ""}${faction.standing} standing`
                    : `${faction.observation.confidence}% confidence`}
                </em>
              </article>
            ))}
          </section>
        )}

        {recentFieldRecords.length > 0 && (
          <section className="field-records" aria-label="Recent expedition field decisions">
            <header><Route size={13} />Recovered field records</header>
            {recentFieldRecords.map((expedition) => {
              const location = state.locations.find((item) => item.id === expedition.location_id);
              const decision = expedition.field_decision;
              const found = expedition.equipment_found;
              return (
                <article key={expedition.id}>
                  <span><strong>{location?.name ?? "Unknown site"}</strong><small>{decision ? titleFromKey(decision.dilemma) : "Recovered equipment"}</small></span>
                  {decision && <p>Standing policy chose <b>{titleFromKey(decision.decision)}</b>. {decision.standing_change ? `${decision.standing_change > 0 ? "+" : ""}${decision.standing_change} faction standing.` : "No faction standing changed."}</p>}
                  {found && <p><Backpack size={11} />Recovered {catalog.equipment[found.item_id]?.name ?? titleFromKey(found.item_id)} at {found.condition}% condition.</p>}
                </article>
              );
            })}
          </section>
        )}

        <section
          className={`recruitment-intel ${!recruitmentAvailable ? "cooldown" : ""}`}
          aria-labelledby="surface-recruitment-title"
        >
          <header>
            <UserPlus aria-hidden="true" />
            <span>
              <small>Community growth</small>
              <strong id="surface-recruitment-title">Rare surface rescues</strong>
            </span>
            {state.recruitment && (
              <em>
                Campaign rescues · {state.recruitment.rescued}{legacyFiniteRecruitment ? `/${state.recruitment.limit}` : " · uncapped"}
              </em>
            )}
          </header>
          {legacyFiniteRecruitment && !recruitmentAvailable ? (
            <p>
              This community has reached its campaign rescue limit. Further
              expeditions cannot add survivors.
            </p>
          ) : !recruitmentAvailable ? (
            <p>
              The community is still settling its latest arrival. Another survivor can join after {formatDuration(recruitmentCooldown)}; expeditions can still provide aid in the field.
            </p>
          ) : (
            <>
              <p>
                There is no direct recruitment. Rare rescue opportunities can
                arise during scavenging expeditions and are never confirmed
                until the outer lock cycles again.
              </p>
              <p>
                Larger teams, strong Scouting and Medicine, cautious orders,
                and trusted surface groups improve the prospects. Nothing is
                guaranteed.
              </p>
              {!legacyFiniteRecruitment && <p>There is no population cap. Beds, provisions, useful work and a defensible shelter are the practical limits.</p>}
            </>
          )}
        </section>

        <fieldset className="roster-picker surface-roster-picker">
          <legend><UsersRound size={14} />Choose the team · {validRoster.length}/3</legend>
          {available.length > 8 && (
            <label className="surface-roster-search">
              <span className="visually-hidden">Search expedition roster</span>
              <input type="search" placeholder="Search available people" value={rosterSearch} onChange={(event) => setRosterSearch(event.currentTarget.value)} />
            </label>
          )}
          {visibleRosterOptions.map((survivor) => {
            const checked = validRoster.includes(survivor.id);
            const proposedRoster = checked
              ? validRoster
              : [...validRoster, survivor.id];
            return (
              <SurvivorRosterOption
                catalog={catalog}
                checked={checked}
                coverageWarning={coverageImpactWarning(state, survivor, proposedRoster)}
                disabled={!checked && validRoster.length >= 3}
                key={survivor.id}
                survivor={survivor}
                onChange={() =>
                  setRoster(
                    checked
                      ? validRoster.filter((id) => id !== survivor.id)
                      : [...validRoster, survivor.id],
                  )
                }
              />
            );
          })}
          {!visibleRosterOptions.length && <p className="quiet-state">No available people match this search.</p>}
          {rosterTotalPages > 1 && (
            <nav className="surface-roster-pagination" aria-label="Expedition roster pages">
              <button type="button" disabled={rosterPage === 1} onClick={() => setRosterPage((page) => page - 1)}>Previous</button>
              <span>{rosterPage}/{rosterTotalPages}</span>
              <button type="button" disabled={rosterPage === rosterTotalPages} onClick={() => setRosterPage((page) => page + 1)}>Next</button>
            </nav>
          )}
        </fieldset>

        <div className="expedition-focus-option">
          <label htmlFor="expedition-focus">
            <span>Search priority</span>
            <select
              id="expedition-focus"
              value={focus}
              onChange={(event) => setFocus(event.target.value as ResourceKey)}
            >
              {(Object.keys(catalog.resources) as ResourceKey[]).map((resource) => (
                <option key={resource} value={resource}>{catalog.resources[resource].name}</option>
              ))}
            </select>
          </label>
          <small>Focused salvage is more likely, never guaranteed.</small>
        </div>
        <div className="expedition-focus-option rescue-posture-option">
          <label htmlFor="rescue-posture">
            <span>Rescue posture</span>
            <select
              id="rescue-posture"
              value={rescuePosture}
              onChange={(event) => setRescuePosture(event.currentTarget.value as "welcome" | "aid_only")}
            >
              <option value="welcome">Welcome survivors</option>
              <option value="aid_only">Field aid only</option>
            </select>
          </label>
          <small>{rescuePosture === "welcome" ? "Offer a place at the bunker if the team completes a rescue." : "Provide help outside without inviting anyone to join the community."}</small>
        </div>
        <fieldset className="expedition-policy-picker">
          <legend>Risk policy · decisions after contact</legend>
          <div>
            {expeditionPolicies.map(({ key, name, summary, tradeoff, icon: Icon }) => (
              <label className={policy === key ? "checked" : ""} key={key}>
                <input
                  type="radio"
                  name="expedition-policy"
                  value={key}
                  checked={policy === key}
                  onChange={() => setPolicy(key)}
                />
                <Icon aria-hidden="true" />
                <span>
                  <strong>{name}</strong>
                  <small>{summary}</small>
                  <em>{tradeoff}</em>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <MissionReadinessPreview
          catalog={catalog}
          focus={focus}
          policy={policy}
          readiness={readiness}
          teamSize={rosterSurvivors.length}
        />
        <p className="warning-copy"><AlertTriangle size={14} />Once they leave, standing policy decides what happens beyond radio contact.</p>
        </div>
        <div className="expedition-action-dock">
          <button className="action-button" disabled={busy || validRoster.length === 0 || selectedReportLocked} onClick={dispatch}>
            <Send size={15} />Send expedition<ArrowRight size={15} />
          </button>
        </div>
      </aside>

      {activeExpeditionScenes.length > 0 && (
        <div className="active-expeditions panel-surface">
          <h3><Route size={15} />Teams beyond the door</h3>
          {activeExpeditionScenes.map(({ expedition, location, people }) => (
            <article key={expedition.id}>
              <span className="signal-pulse" />
              <div>
                <strong>{location.name}</strong>
                <small>{people.map((person) => person.name.split(" ")[0]).join(", ")}</small>
              </div>
              <em>{expeditionContactLabel(expedition)}</em>
              <span><Clock3 size={12} />{expeditionReturnWindow(expedition)}</span>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
